import { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete"
import { loadPlaces, placesLoaded } from "../../utils/google-maps-loader"
import { IconClose, IconGlobe, IconPin } from "./icons"

// City picker for the where step. Uses Places autocomplete in the browser;
// if Maps can't load, it falls back to plain text (the server resolves the
// city either way).
const CityInput = ({ city, remote, onPick, onRemote, onClear }) => {
  const [query, setQuery] = useState("")
  const [ready, setReady] = useState(placesLoaded())
  const [failed, setFailed] = useState(false)
  const latestCity = useRef(city)
  latestCity.current = city

  useEffect(() => {
    if (ready) return undefined
    let alive = true
    loadPlaces().then((ok) => {
      if (!alive) return
      if (ok) setReady(true)
      else setFailed(true)
    })
    return () => {
      alive = false
    }
  }, [ready])

  if (city || remote) {
    return (
      <div className="ob-citychip">
        {remote ? <IconGlobe size={20} /> : <IconPin size={20} />}
        <span>{remote ? "Remote / open to anywhere" : city.label}</span>
        <button type="button" aria-label="Change city" onClick={onClear}>
          <IconClose />
        </button>
      </div>
    )
  }

  // Select right away: the server resolves the city from its name. The
  // browser's coordinates are only its fallback if Places is down, so they're
  // added when (and if) the geocode comes back, for the same city.
  const pick = (label) => {
    setQuery("")
    onPick({ label, lat: null, lng: null })
    geocodeByAddress(label)
      .then((results) => getLatLng(results[0]))
      .then((point) => {
        const current = latestCity.current
        if (current && current.label === label) onPick({ label, lat: point.lat, lng: point.lng })
      })
      .catch(() => {})
  }

  const remoteLink = (
    <button className="ob-linkbtn ob-remote" type="button" onClick={onRemote}>
      I’m fully remote / open to anywhere
    </button>
  )

  if (!ready) {
    return (
      <>
        <input
          id="ob-city"
          className="ob-input"
          type="text"
          autoComplete="off"
          placeholder={failed ? "Your city, e.g. Toronto, ON, Canada" : "Start typing your city"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && query.trim()) {
              e.preventDefault()
              onPick({ label: query.trim(), lat: null, lng: null })
            }
          }}
        />
        {query.trim().length >= 2 && <p className="ob-fine">Press Enter to use “{query.trim()}”.</p>}
        {remoteLink}
      </>
    )
  }

  return (
    <PlacesAutocomplete
      value={query}
      onChange={setQuery}
      // Enter with no suggestion highlighted passes no placeId: use the typed
      // city and let the server look it up
      onSelect={(address, placeId) => {
        if (placeId) pick(address)
        else if (address.trim().length >= 2) pick(address.trim())
      }}
      searchOptions={{ types: ["(cities)"] }}
      highlightFirstSuggestion
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <>
          <input
            {...getInputProps({
              id: "ob-city",
              className: "ob-input",
              placeholder: "Start typing your city",
              autoComplete: "off",
            })}
          />
          {(suggestions.length > 0 || loading) && (
            <div className="ob-sugs">
              {loading && suggestions.length === 0 && <div className="ob-sug">Searching…</div>}
              {suggestions.map((s) => (
                <div
                  {...getSuggestionItemProps(s, {
                    className: classNames("ob-sug", { "ob-active": s.active }),
                  })}
                  // after the spread: the library's own key is undefined
                  key={s.placeId}
                >
                  <span className="ob-sug-ic">
                    <IconPin />
                  </span>
                  <span>{s.description}</span>
                </div>
              ))}
            </div>
          )}
          {remoteLink}
        </>
      )}
    </PlacesAutocomplete>
  )
}

export default CityInput
